import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { Save, FileText, ChevronDown, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const PAGE_SCHEMA = {
  home: {
    label: 'Home Page',
    sections: [
      { key: 'hero_title', label: 'Hero Title', type: 'text', placeholder: 'Develop Your Land' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'text', placeholder: 'Independently.' },
      { key: 'hero_description', label: 'Hero Description', type: 'textarea', placeholder: 'Maximize your property\'s value...' },
      { key: 'about_title', label: 'About Section Title', type: 'text', placeholder: '12 Years of Independent Land Development...' },
      { key: 'about_description', label: 'About Section Description', type: 'textarea', placeholder: 'Founded in 2014...' },
      { key: 'services_title', label: 'Services Section Title', type: 'text', placeholder: 'Our 360° Land Development Solutions.' },
      { key: 'services_subtitle', label: 'Services Section Subtitle', type: 'textarea', placeholder: 'We help you develop...' },
    ]
  },
  about: {
    label: 'About Page',
    sections: [
      { key: 'page_title', label: 'Page Title', type: 'text', placeholder: 'About HOFLICH' },
      { key: 'page_description', label: 'Page Description', type: 'textarea', placeholder: 'Our journey started...' },
      { key: 'vision_title', label: 'Vision Title', type: 'text', placeholder: 'Our Vision' },
      { key: 'vision_description', label: 'Vision Description', type: 'textarea', placeholder: '' },
      { key: 'badges', label: 'Tags / Badges (comma-separated)', type: 'text', placeholder: 'Design, Approvals, Project Loans...' },
    ]
  },
  services: {
    label: 'Services Page',
    sections: [
      { key: 'hero_eyebrow', label: 'Hero Eyebrow Text', type: 'text', placeholder: 'Our Critical Services' },
      { key: 'hero_title', label: 'Hero Title', type: 'text', placeholder: 'A comprehensive 6-pillar approach...' },
      { key: 'hero_description', label: 'Hero Description', type: 'textarea', placeholder: 'We don\'t just get your drawings stamped...' },
      { key: 'cta_title', label: 'CTA Title', type: 'text', placeholder: 'Let\'s structure your next project...' },
      { key: 'cta_description', label: 'CTA Description', type: 'textarea', placeholder: 'Speak to our experts...' },
    ]
  },
  contact: {
    label: 'Contact Page',
    sections: [
      { key: 'hero_eyebrow', label: 'Hero Eyebrow', type: 'text', placeholder: 'Get in touch' },
      { key: 'hero_title', label: 'Hero Title', type: 'text', placeholder: 'Let\'s talk approvals.' },
      { key: 'hero_description', label: 'Hero Description', type: 'textarea', placeholder: 'Reach us by phone...' },
      { key: 'form_heading', label: 'Form Section Heading', type: 'text', placeholder: 'Let\'s simplify your next approval.' },
      { key: 'form_subheading', label: 'Form Section Description', type: 'textarea', placeholder: 'Tell us a little about your project...' },
    ]
  },
  projects: {
    label: 'Projects Page',
    sections: [
      { key: 'ongoing_eyebrow', label: 'Ongoing — Eyebrow Text', type: 'text', placeholder: 'Ongoing Projects' },
      { key: 'ongoing_title', label: 'Ongoing — Title', type: 'text', placeholder: 'Building the future, today.' },
      { key: 'ongoing_description', label: 'Ongoing — Description', type: 'textarea', placeholder: 'Explore the ambitious developments...' },
      { key: 'completed_eyebrow', label: 'Completed — Eyebrow Text', type: 'text', placeholder: 'Completed Projects' },
      { key: 'completed_title', label: 'Completed — Title', type: 'text', placeholder: 'Watch our visions come to life.' },
      { key: 'completed_description', label: 'Completed — Description', type: 'textarea', placeholder: 'Browse our video snippets...' },
    ]
  },
  clients: {
    label: 'Clients Page',
    sections: [
      { key: 'hero_title', label: 'Page Title', type: 'text', placeholder: 'Our Clients' },
      { key: 'hero_description', label: 'Page Description', type: 'textarea', placeholder: 'Trusted by leading developers...' },
      { key: 'client_list', label: 'Client Names (comma-separated)', type: 'textarea', placeholder: 'RMZ CORP, LODHA, GODREJ...' },
    ]
  },
  consultation: {
    label: 'Consultation Page',
    sections: [
      { key: 'hero_title', label: 'Page Title', type: 'text', placeholder: 'Book a Consultation' },
      { key: 'hero_description', label: 'Page Description', type: 'textarea', placeholder: 'Schedule a meeting...' },
    ]
  }
};

const PAGE_KEYS = Object.keys(PAGE_SCHEMA);

export default function PagesManager() {
  const [activePage, setActivePage] = useState('home');
  const [contentMap, setContentMap] = useState({});
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API_URL}/pages`);
      const map = {};
      res.data.forEach(item => {
        const k = `${item.page_slug}__${item.section_key}`;
        map[k] = item;
      });
      setContentMap(map);
      
      // Initialize form values for active page
      initFormValues(activePage, map);
    } catch (err) {
      toast.error("Failed to load page content");
    } finally {
      setLoading(false);
    }
  };

  const initFormValues = (pageSlug, map) => {
    const schema = PAGE_SCHEMA[pageSlug];
    if (!schema) return;
    const values = {};
    schema.sections.forEach(section => {
      const k = `${pageSlug}__${section.key}`;
      values[section.key] = map[k]?.content || '';
    });
    setFormValues(values);
  };

  useEffect(() => {
    fetchPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initFormValues(activePage, contentMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage]);

  const handleSave = async () => {
    setSaving(true);
    const schema = PAGE_SCHEMA[activePage];
    try {
      const promises = schema.sections.map(section => {
        const currentVal = formValues[section.key] || '';
        return axios.post(`${API_URL}/pages`, {
          page_slug: activePage,
          section_key: section.key,
          content: currentVal,
        });
      });
      await Promise.all(promises);
      toast.success(`${schema.label} content saved!`);
      
      // Refresh content map
      const res = await axios.get(`${API_URL}/pages`);
      const map = {};
      res.data.forEach(item => {
        const k = `${item.page_slug}__${item.section_key}`;
        map[k] = item;
      });
      setContentMap(map);
    } catch (err) {
      toast.error("Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  const activeSchema = PAGE_SCHEMA[activePage];
  const hasChanges = activeSchema?.sections.some(section => {
    const k = `${activePage}__${section.key}`;
    const saved = contentMap[k]?.content || '';
    const current = formValues[section.key] || '';
    return saved !== current;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Page Content Manager</h2>
          <p className="text-neutral-400 text-sm">Edit text content for every page on the website.</p>
        </div>
        {hasChanges && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2 text-amber-500 text-sm bg-amber-500/10 px-3 py-1.5 rounded-full"
          >
            <Info size={14} />
            Unsaved changes
          </motion.div>
        )}
      </div>

      {/* Page Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-neutral-800 pb-4">
        {PAGE_KEYS.map(key => {
          const isActive = activePage === key;
          const pageHasContent = PAGE_SCHEMA[key].sections.some(s => {
            const k2 = `${key}__${s.key}`;
            return !!contentMap[k2]?.content;
          });

          return (
            <button
              key={key}
              onClick={() => setActivePage(key)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                isActive
                  ? 'bg-amber-500 text-black'
                  : 'bg-neutral-900 text-neutral-400 hover:text-white hover:bg-neutral-800'
              }`}
            >
              {PAGE_SCHEMA[key].label}
              {pageHasContent && !isActive && (
                <span className="ml-2 w-1.5 h-1.5 bg-green-500 rounded-full inline-block"></span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={activePage}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="bg-neutral-900/50 backdrop-blur-md border border-neutral-800 rounded-2xl p-6 md:p-8"
          >
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-bold text-white flex items-center gap-3">
                <FileText size={22} className="text-amber-500" />
                {activeSchema.label}
              </h3>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className={`px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 transition ${
                  hasChanges
                    ? 'bg-amber-500 text-black hover:bg-amber-600'
                    : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                }`}
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Save size={16} />
                )}
                {saving ? 'Saving...' : 'Save All Changes'}
              </button>
            </div>

            <div className="space-y-6">
              {activeSchema.sections.map(section => {
                const k = `${activePage}__${section.key}`;
                const hasSavedValue = !!contentMap[k]?.content;

                return (
                  <div key={section.key} className="group">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-neutral-300">
                        {section.label}
                      </label>
                      <span className="text-[10px] tracking-wider text-neutral-600 uppercase font-mono">
                        {section.key}
                      </span>
                    </div>
                    {section.type === 'textarea' ? (
                      <textarea
                        rows={4}
                        value={formValues[section.key] || ''}
                        onChange={e => setFormValues({ ...formValues, [section.key]: e.target.value })}
                        placeholder={section.placeholder}
                        className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition placeholder:text-neutral-700 text-sm leading-relaxed resize-none"
                      />
                    ) : (
                      <input
                        type="text"
                        value={formValues[section.key] || ''}
                        onChange={e => setFormValues({ ...formValues, [section.key]: e.target.value })}
                        placeholder={section.placeholder}
                        className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition placeholder:text-neutral-700 text-sm"
                      />
                    )}
                    {hasSavedValue && (
                      <p className="text-[10px] text-green-500/60 mt-1 flex items-center gap-1">
                        <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                        Saved to database
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
